import React, { type ComponentProps, useCallback } from 'react';
import { type Tabs } from 'expo-router';
import type { TextProps } from 'react-native';
import { StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';

import { type IconName } from '../Icon';
import { MainTabBarItem } from './MainTabBarItem';

// ─────────────────────────────────────────────────────────────────────────────

export const MainTabBarV2 = (props: Props) => {
  const {
    state,
    descriptors,
    emitter,
    navigateToTab,
    tabsDisplayingBadge = [],
    ...viewProps
  } = props;

  return (
    <View style={styles.container} accessibilityRole="tablist" {...viewProps}>
      {state.routes.map((route, index) => {
        const { key, name } = route;
        const { options } = descriptors[key];

        // NOTE: Ignores internal/system tabs
        if (!options.title) return null;

        const isActive = state.index === index;
        const hasBadge = tabsDisplayingBadge.includes(name);

        return (
          <MainTabBarItemContainer
            key={key}
            screenKey={key}
            name={name}
            options={options}
            isActive={isActive}
            hasBadge={hasBadge}
            maxFontSizeMultiplier={1.25}
            emitter={emitter}
            navigateToTab={navigateToTab}
          />
        );
      })}
    </View>
  );
};

// ─── Components ──────────────────────────────────────────────────────────────

const MainTabBarItemContainer = (props: ItemContainerProps) => {
  const {
    screenKey,
    name,
    isActive,
    hasBadge,
    maxFontSizeMultiplier,
    options,
    emitter,
    navigateToTab,
  } = props;

  const { tabBarButtonTestID, iconName, title, tabBarAccessibilityLabel, onPress } = options;

  const itemTitle = title ?? name ?? '';

  // ─── Handlers ────────────────────────────────────────────────────────

  const handlePress = useCallback(() => {
    const event = emitter.emit({ type: 'tabPress', target: screenKey, canPreventDefault: true });

    if (event.defaultPrevented) return;

    onPress?.();

    // NOTE: Navigating by route key keeps the params inside the tab screen
    navigateToTab(screenKey);
  }, [emitter, navigateToTab, onPress, screenKey]);

  const handleLongPress = useCallback(() => {
    emitter.emit({ type: 'tabLongPress', target: screenKey });
  }, [emitter, screenKey]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <MainTabBarItem
      testID={tabBarButtonTestID}
      title={itemTitle}
      iconName={iconName}
      accessibilityLabel={tabBarAccessibilityLabel}
      isActive={isActive}
      hasBadge={hasBadge}
      maxFontSizeMultiplier={maxFontSizeMultiplier}
      onPress={handlePress}
      onLongPress={handleLongPress}
    />
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    backgroundColor: theme.colors.APP_BACKGROUND,
    borderTopWidth: 1,
    borderTopColor: theme.colors.OPACITY.DARK_KALE.DARK,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  tabsDisplayingBadge?: readonly string[];
} & TabBarProps;

type ItemContainerProps = Readonly<{
  screenKey: string;
  name: string;
  isActive: boolean;
  hasBadge: boolean;
  emitter: TabBarProps['emitter'];
  navigateToTab: TabBarProps['navigateToTab'];
  options: TabBarProps['descriptors'][number]['options'] & TabBarButtonExtraProps;
  maxFontSizeMultiplier?: TextProps['maxFontSizeMultiplier'];
}>;

export type TabBarButtonExtraProps = {
  iconName?: IconName;
  onPress?: () => void;
};

type TabBarProps = Parameters<TabBarRenderer>[0];
type TabBarRenderer = NonNullable<ComponentProps<typeof Tabs>['tabBar']>;
